<template>
  <div class="ten-pagination">
    <span
      v-if="showTotal"
      class="ten-pagination__total">
      <!-- @slot 总数文案 -->
      <slot name="total" :total="total">
        {{ t('pagination.total', [total]) }}
      </slot>
    </span>
    <div class="ten-pagination__btn-wrapper">
      <ten-button
        size="small"
        icon="left"
        icon-only
        :class="buttonClass"
        :disabled="disabled || index === 1"
        @click="prevPage"
      />

      <template v-if="!simple">
        <ten-button
          v-if="isFolded"
          size="small"
          :class="buttonClass"
          :type="getButtonType(1)"
          :disabled="disabled"
          @click="toPage(1)"
        >
          1
        </ten-button>
        <ten-button
          v-show="isFolded && isPrevMoreShow"
          :class="[buttonClass, 'ten-pagination__btn-prev-more']"
          size="small"
          icon="more"
          icon-only
          :disabled="disabled"
          @click="prevMorePage"
        />

        <ten-button
          size="small"
          v-for="i in pages"
          :key="i"
          :class="buttonClass"
          :type="getButtonType(i)"
          :disabled="disabled"
          @click="toPage(i)"
        >
          {{ i }}
        </ten-button>

        <ten-button
          :class="[buttonClass, 'ten-pagination__btn-next-more']"
          v-show="isFolded && isNextMoreShow"
          size="small"
          icon="more"
          icon-only
          :disabled="disabled"
          @click="nextMorePage"
        />
        <ten-button
          v-if="isFolded"
          size="small"
          :class="buttonClass"
          :type="getButtonType(_pageCount)"
          :disabled="disabled"
          @click="toPage(_pageCount)"
        >
          {{ _pageCount }}
        </ten-button>
      </template>
      <template v-else>
        <div class="ten-pagination__simple">
          <ten-input
            v-model="jumpIndex"
            class="ten-pagination__simple-input"
            type="number"
            size="small"
            :min="1"
            :disabled="disabled"
            @change="jumpToPage"
          />
          <span class="ten-pagination__simple-total">/ {{ _pageCount }}</span>
        </div>
      </template>

      <ten-button
        size="small"
        icon="right"
        icon-only
        :class="buttonClass"
        :disabled="disabled || index === _pageCount"
        @click="nextPage"
      />
    </div>

    <template v-if="pageSizeOption && pageSizeOption.length">
      <ten-select
        size="small"
        class="ten-pagination__page-size-selector"
        :value="pageSize"
        :disabled="disabled"
        @change="onSelectorChange"
      >
        <ten-select-option
          v-for="(value, i) in _pageSizeOption"
          :value="value"
          :label="t('pagination.pageSize', [value])"
          :key="i"
        />
      </ten-select>
    </template>

    <template v-if="showJumper">
      <div class="ten-pagination__jumper">
        {{t('pagination.goto')}}
        <ten-input
          v-model="jumpIndex"
          class="ten-pagination__jumper-input"
          type="number"
          size="small"
          :min="1"
          :disabled="disabled"
          @change="jumpToPage"
        />
        {{t('pagination.pageUnit')}}
      </div>
    </template>
  </div>
</template>

<script>
import Input from '../input';
import Button from '../button';
import Select from '../select';
import { localMixin } from '../config';

const name = 'ten-pagination';

export default {
  name,
  components: {
    TenInput: Input,
    TenButton: Button,
    TenSelect: Select,
    TenSelectOption: Select.SelectOption,
  },
  mixins: [localMixin],
  model: {
    prop: 'index',
    event: 'change',
  },
  props: {
    /**
     * 当前页
     *
     * @model
     */
    index: {
      type: Number,
      default: 1,
    },
    /**
     * 分页大小。
     * 配置 pageSizeOption 时，支持 .sync 更新
     */
    pageSize: {
      type: Number,
      default: 10,
    },
    /**
     * 显示总条数
     */
    showTotal: {
      type: Boolean,
      default: false,
    },
    /**
     * 总记录数
     */
    total: Number,
    /**
     * 简洁模式
     */
    simple: Boolean,
    /**
     * 页数
     */
    pageCount: Number,
    /**
     * 可选分页大小
     */
    pageSizeOption: Array,
    /**
     * 最多显示页码按钮数
     */
    maxPageBtn: {
      type: Number,
      default: 10,
    },
    /**
     * 折叠时最多显示页码按钮数
     */
    foldedMaxPageBtn: {
      type: Number,
      default: 5,
    },
    /**
     * 显示跳转输入框
     */
    showJumper: {
      type: Boolean,
      default: false,
    },
    /**
     * 禁用分页
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      jumpIndex: this.index,
      buttonClass: 'ten-pagination__btn',
    };
  },
  computed: {
    _pageCount() {
      const c = this.pageCount || Math.ceil(this.total / this.pageSize);
      return c > 0 ? c : 1;
    },

    _pageSizeOption() {
      return this.pageSizeOption.find(v => v === this.pageSize)
        ? this.pageSizeOption
        : this.pageSizeOption.concat(this.pageSize).sort((a, b) => (typeof a === 'number' && typeof b === 'number') ? a - b : -1); // eslint-disable-line
    },

    curPageLeftCount() {
      return Math.ceil((this.foldedMaxPageBtn - 1) / 2);
    },

    curPageRightCount() {
      return parseInt((this.foldedMaxPageBtn - 1) / 2, 10);
    },

    isPrevMoreShow() {
      return 2 + this.curPageLeftCount < this.index;
    },

    isNextMoreShow() {
      return this._pageCount - 1 - this.curPageRightCount > this.index;
    },

    pages() {
      const array = [];
      let start;
      let end;

      if (this.isFolded) {
        if (this.isPrevMoreShow && this.isNextMoreShow) {
          start = this.index - this.curPageLeftCount;
          end = this.index + this.curPageRightCount;
        } else {
          start = this.isPrevMoreShow ? this._pageCount - this.foldedMaxPageBtn + 1 : 2;
          end = this.isPrevMoreShow ? this._pageCount - 1 : this.foldedMaxPageBtn;
        }
      } else {
        start = 1;
        end = this._pageCount;
      }

      for (let i = start; i <= end; i++) {
        array.push(i);
      }
      return array;
    },
    isFolded() {
      return this._pageCount > this.maxPageBtn;
    },
  },
  watch: {
    index: {
      handler(v) {
        this.jumpIndex = v;
      },
      immediate: true,
    },
  },
  methods: {
    toPage(pageIndex) {
      if (pageIndex < 1) {
        pageIndex = 1;
      } else if (pageIndex > this._pageCount) {
        pageIndex = this._pageCount;
      }
      if (this.index !== pageIndex) {
        this.$emit('change', pageIndex);
      } else {
        this.jumpIndex = pageIndex;
      }
    },
    prevPage() {
      this.toPage(this.index - 1);
    },
    nextPage() {
      this.toPage(this.index + 1);
    },
    prevMorePage() {
      this.toPage(this.index - this.foldedMaxPageBtn);
    },
    nextMorePage() {
      this.toPage(this.index + this.foldedMaxPageBtn);
    },
    jumpToPage() {
      this.toPage(Number(this.jumpIndex));
    },
    getButtonType(index) {
      return this.index === index ? 'primary' : '';
    },
    onSelectorChange(pageSize) {
      let pageCount = 1;
      if (pageSize > 0) {
        pageCount = Math.ceil(this.total / pageSize);
      }

      let isIndexChange = false;

      if (this.index > pageCount) {
        isIndexChange = true;
        /**
         * 当前页变化触发的事件
         * @param {Number} index 当前页码
         */
        this.$emit('change', pageCount);
      }
      /**
       * 分页大小变化事件
       * @param {Number} pageSize 分页大小
       * @param {Number} index 当前页码
       */
      this.$emit('pageSizeChange', pageSize, isIndexChange ? pageCount : this.index);
    },
  },
};
</script>
